import { currencyFormatter, numberFormatter } from '@root/helpers/formatters';
import { escape } from 'lodash-es';

import type { ScheduleItemData } from './types';

// The mention token is always a <span> (see helpers/editor/create-mention.ts) — every nested
// element here must stay inline-safe (<span>, never <div>/<p>), or the parser hoists block
// children out of the mention span and the content renders unstyled outside it.

const formatScheduleItemValue = (item: ScheduleItemData): string | undefined => {
  switch (item.value_type) {
    case 'Amount': {
      // Either half of an amount can be missing while the broker is still filling the form, and
      // whichever half they HAVE entered is shown — a currency on its own, or a number on its own.
      // currencyFormatter is used only once a currency is actually set: left to itself it falls
      // back to USD, which would print a currency nobody chose into a contract document.
      const { amount, currency } = item.value_amount ?? {};
      const hasAmount = amount !== undefined && amount !== null;
      const formatted = hasAmount
        ? currency
          ? currencyFormatter({ amount, currency })
          : numberFormatter(amount)
        : false;

      return formatted || currency || undefined;
    }
    case 'Percentage': {
      const percentage = item.value_percentage;

      return percentage === undefined || percentage === null ? undefined : `${percentage}%`;
    }
    case 'Timeframe': {
      // Same rule as Amount above — whichever half was entered is shown. Filtering on truthiness
      // would drop a value of 0, which the schema and the editor both allow, leaving a bare unit.
      const { value, unit } = item.value_timeframe ?? {};
      const parts = [value, unit].filter((part) => part !== undefined && part !== null && String(part).trim() !== '');

      return parts.length > 0 ? parts.join(' ') : undefined;
    }
    case 'Other':
      return item.value_type_other || undefined;
    default:
      return undefined;
  }
};

const formatScheduleItemBasis = (item: ScheduleItemData): string | undefined => {
  if (!item.basis?.length) return undefined;

  const parts = item.basis
    .map((entry) => (entry === 'Other' ? item.basis_other : entry))
    .filter((part) => part !== undefined && part !== null && String(part).trim() !== '');

  return parts.length > 0 ? parts.join(', ') : undefined;
};

/**
 * Longest value that still fits the value column, in characters. A longer value gets its own
 * full-width line with the title block below it, rather than being wrapped mid-number into a
 * column a fraction of its width.
 *
 * Measured, not guessed: the column caps at `--schedule-value-max` (9em = 108px) in
 * contract-mentions.css, and in Gotham Bold at the rendered 12px the widest 14-character value
 * ("GBP 00,000,000") is 107px while a 15-character one ("GBP 100,000,000") is 112px. Re-measure
 * both together if either the cap or the font changes — and err low, since a value that takes its
 * own line unnecessarily still reads fine, whereas one left in the column breaks mid-digit.
 */
const MAX_VALUE_CHARS = 14;

/**
 * Text length past which a row is allowed to break across a PDF page.
 *
 * Rows are kept whole by default (`break-inside: avoid`), but an unbreakable box taller than a
 * page cannot be placed at all — Chromium then paints it over whatever follows. Height depends on
 * the width the row has, and a level-3 row is the narrowest: at roughly 15 characters a line and
 * ~59 lines to an A4 content area, it runs out of page at about 900 characters. Half that is the
 * threshold, so the escape hatch opens well before the overlap can happen, while ordinary
 * descriptions — a sentence or two — stay protected.
 */
const MAX_UNBREAKABLE_CHARS = 450;

/**
 * What a hard line break costs against that budget. `.mention-data` is `white-space: pre-line`, so
 * a newline a broker typed is a real line in the PDF: 60 short lines fill a page on about 120
 * characters, which plain length would wave through. Each one is charged a full narrow line.
 */
const CHARS_PER_LINE = 15;

const countNewlines = (text = '') => text.split('\n').length - 1;

const renderScheduleItem = (item: ScheduleItemData): string => {
  const value = formatScheduleItemValue(item);
  const basis = formatScheduleItemBasis(item);
  const children = (item.children ?? []).map(renderScheduleItem).join('');

  // Every interpolated value is broker-entered free text, so it is escaped on the way in. The SPA
  // path runs the result through sanitizeHtml (replace-mention.ts) but the PDF path assigns it to
  // innerHTML directly (replaceContractVariables.ts), and either way an unescaped `<` or `&` in a
  // title corrupts the surrounding block. Escaping here makes the output correct for both callers.
  // <strong>, not a font-weight class, matches how bold already works elsewhere in block content
  // (e.g. existing table cells use literal <strong> rather than relying on CSS specificity).
  const isLongValue = Boolean(value && value.length > MAX_VALUE_CHARS);
  const rowText = [value, item.title, basis, item.description].filter(Boolean).join('\n');
  // Height, roughly: the text it holds, plus a full line for every break the broker typed.
  const rowTextLength = rowText.length + countNewlines(item.description) * CHARS_PER_LINE;
  const rowClass = [
    'schedule-mention-item-row',
    isLongValue && 'schedule-mention-item-row--long-value',
    rowTextLength > MAX_UNBREAKABLE_CHARS && 'schedule-mention-item-row--long-content',
  ]
    .filter(Boolean)
    .join(' ');

  const titleMarkup = item.title
    ? `<span class="schedule-mention-item-title"><strong>${escape(item.title)}</strong></span>`
    : '';

  // A long value takes a full-width line of its own, and the title rides along on it — so it moves
  // into the value cell, leaving basis and description in the title cell where they keep the
  // group's normal second-column offset. Reading order is unchanged either way.
  const valueContent = value ? `<strong>${escape(value)}</strong>${isLongValue ? titleMarkup : ''}` : '';
  const titleCellContent =
    (isLongValue ? '' : titleMarkup) +
    (basis ? `<span class="schedule-mention-item-basis">${escape(basis)}</span>` : '') +
    (item.description ? `<span class="schedule-mention-item-description">${escape(item.description)}</span>` : '');

  // A two-cell row (see contract-mentions.css): value is one cell, title+basis+description share
  // the second so basis/description align under the title, not under the value.
  // BOTH cells are emitted whenever the row renders, even when the value is empty. An empty value
  // cell is hidden by CSS (the title then starts where a value would have), so this keeps the
  // markup uniform and leaves that layout decision in the stylesheet.
  // Kept whitespace-free between tags: `.mention-data` sets `white-space: pre-line`, so any
  // literal newline/indentation here would render as a visible blank line.
  return (
    '<span class="schedule-mention-item">' +
    (valueContent || titleCellContent
      ? `<span class="${rowClass}">` +
        `<span class="schedule-mention-item-value">${valueContent}</span>` +
        `<span class="schedule-mention-item-title-cell">${titleCellContent}</span>` +
        '</span>'
      : '') +
    (children ? `<span class="schedule-mention-item-children">${children}</span>` : '') +
    '</span>'
  );
};

export const renderScheduleMentionHtml = (items: ScheduleItemData[] = []): string =>
  items.map(renderScheduleItem).join('');
